import { state } from "./state";
import { LEVELS } from "./levels";
import { starsFor } from "./scoring";
import { $ } from "../dom";
import { buildBoard, renderBoard } from "./board";
import { openReveal, prepareSolves, resetReveal } from "../reveal/reveal";

/** Total gold of the roster as posted right now (unposted heroes earn 0). */
export function currentTotal(): number {
  const v = state.values();
  return state.assign.reduce((sum, j, i) => (j >= 0 ? sum + v[i][j] : sum), 0);
}

/* ---------- Level load ---------- */
export function loadLevel(i: number): void {
  state.idx = i;
  state.assign = [...state.saved[i]];
  state.selected = -1;
  buildBoard();
  recompute();
  updateNav();
}

/* ---------- Recompute (render + HUD + stars + persistence) ---------- */
export function recompute(): void {
  const L = state.level();
  const n = state.n();
  const posted = state.assign.filter((j) => j >= 0).length;
  const complete = posted === n;
  const total = currentTotal();

  renderBoard();

  // persist the working roster; fold a completed roster into the best record
  state.saved[state.idx] = [...state.assign];
  const rec = state.records[state.idx];
  if (complete && total > rec.total) {
    state.records[state.idx] = { assign: [...state.assign], total };
  }
  const best = state.records[state.idx];

  // HUD
  $("levelVal").textContent = (state.idx + 1) + " / " + LEVELS.length;
  $("goldVal").textContent = String(total);
  $("hiredVal").textContent = posted + " / " + n;
  $("levelName").innerHTML = "Day " + (state.idx + 1) + " — <b>" + L.name + "</b>";
  $("bestLine").textContent = best.assign.length
    ? `Best ledger today: ${best.total} gold`
    : "No completed roster yet";

  // stars rate the best completed roster of this day
  const stars = starsFor(L, best.total);
  const starEls = $("stars").children;
  for (let i = 0; i < 3; i++) starEls[i].classList.toggle("on", i < stars);

  // message
  const msg = $("msg");
  msg.className = "msg";
  if (state.selected >= 0) {
    const h = state.heroes()[state.selected];
    msg.textContent = `${h.name.split(" ")[0]} is up — every contract shows what it would pay them. Tap one to post.`;
  } else if (!complete) {
    msg.textContent = posted === 0
      ? "Tap a hero to see what every contract would pay them."
      : `Every contract needs exactly one hero — ${posted} of ${n} posted.`;
  } else if (total >= L.opt) {
    msg.classList.add("optimal");
    msg.textContent = "✦ The proven-best roster — the guild could not earn a coin more.";
  } else if (total >= L.greedy) {
    msg.textContent = "A strong ledger — but a richer roster exists. Try swapping two heroes.";
  } else {
    msg.textContent = "All contracts filled, but gold was left on the table — the stars hide in the swaps.";
  }

  if (stars === 3 && !state.completed[state.idx]) {
    state.completed[state.idx] = true;
    updateNav();
  }
  $("nextBtn").classList.toggle("glow", stars === 3);
}

/* ---------- Nav ---------- */
export function buildDots(): void {
  const d = $("dots");
  d.innerHTML = "";
  LEVELS.forEach((_, i) => {
    const b = document.createElement("div");
    b.className = "dot";
    b.textContent = String(i + 1);
    b.addEventListener("click", () => loadLevel(i));
    d.appendChild(b);
  });
}

export function updateNav(): void {
  const dots = $("dots").children;
  for (let i = 0; i < dots.length; i++) {
    dots[i].classList.toggle("current", i === state.idx);
    dots[i].classList.toggle("done", state.completed[i]);
    dots[i].textContent = state.completed[i] ? "✓" : String(i + 1);
  }
  ($("prevBtn") as HTMLButtonElement).disabled = state.idx === 0;
  $("nextBtn").textContent = state.idx === LEVELS.length - 1 ? "Face the optimizer ✦" : "Next ▸";
}

export function goPrev(): void {
  if (state.idx > 0) loadLevel(state.idx - 1);
}

export function goNext(): void {
  if (state.idx < LEVELS.length - 1) {
    loadLevel(state.idx + 1);
    return;
  }
  // the reveal compares completed rosters, so every day needs one
  const missing = state.records.findIndex((r) => r.assign.length === 0);
  if (missing === -1) {
    openReveal();
  } else {
    loadLevel(missing);
    const msg = $("msg");
    msg.className = "msg warn";
    msg.textContent = `Day ${missing + 1} has no completed roster yet — fill every contract to face the optimizer.`;
  }
}

export function resetLevel(): void {
  state.assign = new Array<number>(state.n()).fill(-1);
  state.selected = -1;
  recompute();
}

/* ---------- Run lifecycle ---------- */
export function startGame(): void {
  state.phase = "play";
  loadLevel(0);
  // kick off the per-day Hungarian solves (in their Web Worker) during play
  // so the reveal is instant. Failures are swallowed here — the reveal
  // re-awaits each solve and shows a graceful message instead.
  prepareSolves().catch(() => { /* solver warm-up failed; handled at reveal time */ });
}

export function restartGame(): void {
  state.completed = LEVELS.map(() => false);
  state.saved = LEVELS.map((L) => new Array<number>(L.heroCount).fill(-1));
  state.records = LEVELS.map(() => ({ assign: [], total: 0 }));
  $("solverOverlay").classList.add("hidden");
  $("learnOverlay").classList.add("hidden");
  resetReveal();
  loadLevel(0);
}
